import type { VoidFunctionComponent, CSSProperties } from 'react';
import { useSelectFileMatchForRevision } from './hooks';
import Origin from './Origin';
import License from './License';
import FileSelector from './FileSelector';
import FileContents from './FileContents';
import styles from './FileViewer.module.less';

type Props = {
  revisionId: string;
  licenseId: string;
  minHeight?: CSSProperties['minHeight'];
};

/**
 * FileViewer is a generic component for displaying the matched
 * files for a given revision + license.
 *
 * It makes requests to several endpoints for the various data it displays
 * about a given license, and the file matches for that license in this revision.
 *
 *   1. GET `/api/matches` -- for the list of file matches.
 *   2. GET `/api/licenses/:licenseId` -- for additional info about the license itself.
 *   3. GET `/api/revisions/:revisionId/blob/:filePath` -- For the file contents.
 *
 * These all exist as their own hooks, if you need to use them independent of this component.
 */
const FileViewer: VoidFunctionComponent<Props> = ({ revisionId, licenseId, minHeight }) => {
  const {
    isLoading,
    matchOptions,
    isDeclaredLicense,
    selectedMatch,
    selectedMatchOption,
    onSelectedMatchChange,
    isCliLicenseScanWithMatchData,
  } = useSelectFileMatchForRevision(revisionId, licenseId);

  return (
    <div className={styles.container} style={{ minHeight }}>
      <Origin isLoading={isLoading} isDeclared={isDeclaredLicense} />
      <License licenseId={licenseId} />
      {!isLoading && (!matchOptions.length || !selectedMatch) ? (
        <div className={styles.noMatchesFound}>
          No files found matching this license.
        </div>
      ) : (
        <FileSelector
          isLoading={isLoading}
          selectedOption={selectedMatchOption || null}
          options={matchOptions}
          onChange={onSelectedMatchChange}
        />
      )}
      {selectedMatch && (
        <FileContents
          revisionLocator={revisionId}
          filePath={selectedMatch.path}
          sources={selectedMatch.sources}
          isCliLicenseScanWithMatchData={isCliLicenseScanWithMatchData}
        />
      )}
    </div>
  );
};

export default FileViewer;
